import {
  Button,
  Divider,
  InputAdornment,
  InputLabel,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DialogWindow } from "UI/DialogWindow";
import { EditActionButtons } from "UI/EditActionButtons";
import { FormCheckbox } from "UI/FormCheckbox";
import { FormMaskedTextField } from "UI/FormMaskedTextField";
import { FormMoneyField } from "UI/FormMoneyField";
import { FormSelectField } from "UI/FormSelectField";
import { FormTextField } from "UI/FormTextField";
import { API } from "aws-amplify";
import { useCreateApiCall } from "hooks/useCreateApiCall";
import { useEditItem } from "hooks/useEditItem";
import { states } from "models/states";
import { phoneNumberValidation, required, zipCodeValidation } from "models/validations";
import { useContext, useEffect, useState } from "react";
import { GlobalContext } from "store/globalContext";
import { useAppDispatch } from "store/hooks";
import { showError } from "store/reducers/errorReducer";
import { AddNewRegionDialog } from "./AddNewRegionDialog";
import { ModalSpinner } from "./ModalSpinner";

/** @type {import("types/commonTypes").Organization}*/
const defaultFormData = {
  id: "",
  name: "",
  street: "",
  street2: "",
  city: "",
  state: "",
  zipCode: "",
  phone: "",
  email: "",
  speciality: "",
  website: "",
  additionalNotes: "",
  marketingRegionId: "",
  notification_price: 0,
  question_price: 0,
  referral_price: 0,
  button_device_price: 0,
  number_of_free_clicks: 0,
  isFlowSimplified: false,
  isInvoicingDisabled: true,
  creatorRecipientId: null,
  externalFormLink: null,
};

export const EditPracticeDialog = ({
  open,
  practiceId,
  handleClose,
  refreshList,
  regionsOptions,
  refreshRegionsOptions,
  isRecipient = false,
}) => {
  const { createApiCall } = useCreateApiCall();
  const dispatch = useAppDispatch();
  const { showWarning, openConfirmationWithCallback, userData, loadUserData } =
    useContext(GlobalContext);
  const [isAddNewRegionDialogOpen, setIsAddNewRegionDialogOpen] =
    useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const {
    previousData,
    setPreviousData,
    control,
    getValues,
    handleSubmit,
    isDirty,
    reset,
    handleCancel,
    watch,
  } = useEditItem({ defaultFormData, handleClose });

  const deleteOrganization = createApiCall(async () => {
    const organization = getValues();
    const body = {
      organization,
    };

    const result = await API.del("restApi", "/organizations", {
      body,
    });

    if (result?.error) {
      console.error(result.error);
      dispatch(showError(result.error));
      return;
    }

    showWarning({
      title: (
        <Stack justifyContent={"center"} alignItems={"center"} gap={3}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Organization deleted successfully
          </Typography>
        </Stack>
      ),
      content: <></>,
    });

    refreshList();
    handleClose();
  });

  const handleDelete = () => {
    openConfirmationWithCallback({
      title: "Warning",
      message:
        "This practice, its prodivers, senders, and all current buttons will be deleted. Proceed?",
      callbackFn: () => {
        deleteOrganization();
      },
      buttonLabels: ["No", "Yes"],
    });
  };

  const onSubmit = createApiCall(async () => {
    const organization = getValues();

    const result = await API.post("restApi", "/organizations", {
      body: {
        organization,
      },
    });

    if (result?.error) {
      console.error(result.error);
      dispatch(showError(result.error));
      return;
    }

    refreshList();
    handleClose();
    loadUserData(userData?.cognitoId);
  });

  useEffect(() => {
    const loadData = createApiCall(
      async () => {
        setIsLoading(true);
        const result = await API.get(
          "restApi",
          `/recipients/${practiceId}`,
          {}
        );
        const data = result?.Item ?? defaultFormData;
        reset(data);
        setPreviousData(data);
        setIsLoading(false);
      },
      () => setIsLoading(false)
    );

    if (practiceId && open) {
      loadData();
    } else {
      reset(defaultFormData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId, open]);

  const isSuperAdmin =
    userData?.role === "Super Admin" ||
    userData?.email === "admin@referral-stream.com";
  const creatorRecipientId = watch("creatorRecipientId");

  const isPracticeEditable =
    isSuperAdmin ||
    userData?.organizationId === creatorRecipientId ||
    practiceId === userData?.organizationId;

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <DialogWindow
      open={open}
      withCloseButton
      handleClose={handleCancel}
      title={"Edit Practice"}
      content={
        <>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack
              direction={fullScreen ? "column" : "row"}
              gap={3}
              mt={2}
              maxWidth={"1024px"}
            >
              <Stack width="100%" gap={2}>
                {/* <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="name">ID</InputLabel>
                <FormTextField
                  id="id"
                  name="id"
                  control={control}
                  rules={{ required }}
                  disabled
                />
              </Stack> */}
                <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="name">Practice Name</InputLabel>
                  <FormTextField
                    id="name"
                    name="name"
                    control={control}
                    rules={{ required }}
                    InputProps={{
                      autoComplete: "off",
                    }}
                    disabled={!isPracticeEditable}
                  />
                </Stack>
                <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="street">Address Line 1</InputLabel>
                  <FormTextField
                    id="street"
                    name="street"
                    control={control}
                    rules={{ required }}
                    InputProps={{
                      autoComplete: "off",
                    }}
                    disabled={!isPracticeEditable}
                  />
                </Stack>
                <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="street2">Address Line 2</InputLabel>
                  <FormTextField
                    id="street2"
                    name="street2"
                    control={control}
                    InputProps={{
                      autoComplete: "off",
                    }}
                    disabled={!isPracticeEditable}
                  />
                </Stack>
                <Stack direction={"row"} gap={1}>
                  <Stack alignItems={"flex-start"}>
                    <InputLabel htmlFor="city">City</InputLabel>
                    <FormTextField
                      id="city"
                      name="city"
                      control={control}
                      rules={{ required }}
                      InputProps={{
                        autoComplete: "off",
                      }}
                      disabled={!isPracticeEditable}
                    />
                  </Stack>
                  <Stack alignItems={"flex-start"} maxWidth={"35%"}>
                    <InputLabel htmlFor="state">State</InputLabel>
                    <FormSelectField
                      id="state"
                      name="state"
                      control={control}
                      options={states}
                      rules={{ required }}
                      disabled={!isPracticeEditable}
                    />
                  </Stack>
                  <Stack alignItems={"flex-start"}>
                    <InputLabel htmlFor="zipCode">Zip Code</InputLabel>
                    <FormTextField
                      id="zipCode"
                      name="zipCode"
                      control={control}
                      rules={zipCodeValidation}
                      InputProps={{
                        autoComplete: "off",
                      }}
                      disabled={!isPracticeEditable}
                    />
                  </Stack>
                </Stack>
                {/* {isRecipient && isSuperAdmin && (
                  <>
                    <Divider />
                    <Stack alignItems={"flex-start"} flex="1">
                      <InputLabel htmlFor="externalFormLink">
                        Additional Form Link (optional)
                      </InputLabel>
                      <FormTextField
                        id="externalFormLink"
                        name="externalFormLink"
                        control={control}
                        InputProps={{
                          autoComplete: "off",
                        }}
                        disabled={!isPracticeEditable}
                      />
                    </Stack>
                  </>
                )} */}
                <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="phone">Main Phone</InputLabel>
                  <FormMaskedTextField
                    id="phone"
                    name="phone"
                    control={control}
                    rules={phoneNumberValidation}
                    mask="999-999-9999"
                    InputProps={{
                      autoComplete: "off",
                    }}
                    disabled={!isPracticeEditable}
                  />
                </Stack>
              </Stack>
              <Stack width="100%" gap={2}>
                {/* <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="email">
                    Email contact
                  </InputLabel>
                  <FormTextField
                    id="email"
                    name="email"
                    control={control}
                    rules={notRequiredEmailValidation}
                    InputProps={{
                      autoComplete: "off",
                    }}
                  />
                </Stack> */}
                {/* <Stack
                  direction={"row"}
                  gap={2}
                  justifyContent={"space-between"}
                > */}
                <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="speciality">
                    Speciality (optional)
                  </InputLabel>
                  <FormTextField
                    id="speciality"
                    name="speciality"
                    control={control}
                    disabled={!isPracticeEditable}
                  />
                </Stack>
                <Stack mb={1} alignItems={"flex-start"} flex="1">
                  <InputLabel htmlFor="website">Website (optional)</InputLabel>
                  <FormTextField
                    id="website"
                    name="website"
                    control={control}
                    InputProps={{
                      autoComplete: "off",
                    }}
                    disabled={!isPracticeEditable}
                  />
                </Stack>
                {/* {isRecipient && !isSuperAdmin && (
                  <>
                    <Stack alignItems={"flex-start"} flex="1">
                      <InputLabel htmlFor="externalFormLink">
                        Additional Form Link (optional)
                      </InputLabel>
                      <FormTextField
                        id="externalFormLink"
                        name="externalFormLink"
                        control={control}
                        InputProps={{
                          autoComplete: "off",
                        }}
                        disabled={!isPracticeEditable}
                      />
                    </Stack>
                  </>
                )} */}
                {/* </Stack> */}
                {isSuperAdmin && isRecipient && (
                  <>
                    <Divider />
                    <Stack direction={"row"} gap={1}>
                      <Stack alignItems={"flex-start"} flex={1}>
                        <InputLabel htmlFor="notification_price">
                          Notification
                        </InputLabel>
                        <FormMoneyField
                          id="notification_price"
                          name="notification_price"
                          control={control}
                          InputProps={{
                            autoComplete: "new-password",
                            inputProps: {
                              min: 0,
                              max: 10000,
                              step: 1,
                            },
                            startAdornment: (
                              <InputAdornment position="start">
                                $
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Stack>
                      <Stack alignItems={"flex-start"} flex={1}>
                        <InputLabel htmlFor="question_price">
                          Question
                        </InputLabel>
                        <FormMoneyField
                          id="question_price"
                          name="question_price"
                          control={control}
                          InputProps={{
                            autoComplete: "new-password",
                            inputProps: {
                              min: 0,
                              max: 10000,
                              step: 1,
                            },
                            startAdornment: (
                              <InputAdornment position="start">
                                $
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Stack>
                      <Stack alignItems={"flex-start"} flex={1}>
                        <InputLabel htmlFor="referral_price">
                          Referral
                        </InputLabel>
                        <FormMoneyField
                          id="referral_price"
                          name="referral_price"
                          control={control}
                          InputProps={{
                            autoComplete: "new-password",
                            inputProps: {
                              min: 0,
                              max: 10000,
                              step: 1,
                            },
                            startAdornment: (
                              <InputAdornment position="start">
                                $
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Stack>
                    </Stack>
                    <Stack direction={"row"} flexWrap={"wrap"} gap={1}>
                      <Stack alignItems={"flex-start"} flex={1}>
                        <InputLabel htmlFor="button_device_price">
                          Button Device
                        </InputLabel>
                        <FormMoneyField
                          id="button_device_price"
                          name="button_device_price"
                          control={control}
                          InputProps={{
                            autoComplete: "new-password",
                            inputProps: {
                              min: 0,
                              max: 10000,
                              step: 1,
                            },
                            startAdornment: (
                              <InputAdornment position="start">
                                $
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Stack>
                      <Stack alignItems={"flex-start"} flex={1}>
                        <InputLabel htmlFor="number_of_free_clicks">
                          Number Of Free Clicks
                        </InputLabel>
                        <FormMoneyField
                          id="number_of_free_clicks"
                          name="number_of_free_clicks"
                          control={control}
                          InputProps={{
                            autoComplete: "new-password",
                            inputProps: {
                              min: 0,
                              max: 10000,
                              step: 1,
                            },
                          }}
                        />
                      </Stack>
                    </Stack>
                  </>
                )}
                {!isSuperAdmin &&
                  !isRecipient &&
                  !!regionsOptions &&
                  Object.keys(regionsOptions)?.length && (
                    <Stack direction={"row"} gap={2} alignItems={"flex-end"}>
                      <Stack alignItems={"flex-start"} flex="1">
                        <InputLabel htmlFor="marketingRegionId">
                          Marketing Region
                        </InputLabel>
                        <FormSelectField
                          id="marketingRegionId"
                          name="marketingRegionId"
                          control={control}
                          options={regionsOptions}
                          withNone
                        />
                      </Stack>
                      <Button
                        sx={{ padding: "10px" }}
                        onClick={() => setIsAddNewRegionDialogOpen(true)}
                      >
                        Add new region
                      </Button>
                    </Stack>
                  )}
                {/* <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="additionalNotes">
                    Additional notes
                  </InputLabel>
                  <FormTextField
                    id="additionalNotes"
                    name="additionalNotes"
                    control={control}
                  />
                </Stack> */}
                {isSuperAdmin && isRecipient && (
                  <Stack alignItems={"flex-start"}>
                    <FormCheckbox
                      name="isInvoicingDisabled"
                      control={control}
                      label={"Automatic invoicing disabled."}
                      disabled={!isPracticeEditable}
                    />
                  </Stack>
                )}
              </Stack>
            </Stack>
          </form>
          <AddNewRegionDialog
            open={isAddNewRegionDialogOpen}
            handleClose={() => setIsAddNewRegionDialogOpen(false)}
            refreshList={refreshRegionsOptions}
          />
          {isLoading && <ModalSpinner open />}
        </>
      }
      actions={
        <EditActionButtons
          handleCancel={handleCancel}
          handleDelete={handleDelete}
          isDeleteButtonPresent={isSuperAdmin}
          previousData={previousData}
          isDirty={isDirty}
          reset={reset}
          saveChanges={handleSubmit(onSubmit)}
        />
      }
    />
  );
};
